import { gql, useQuery } from '@apollo/client'
import { analyticsApolloClient } from 'graphql/thegraph/apollo'
import { useMemo, useState } from 'react'
import styled from 'styled-components/macro'

const POSITION_PRICE_HISTORY_QUERY = gql`
  query PositionPriceHistory($id: ID!) {
    pool(id: $id) {
      token0Price
      token1Price
      poolDayData(first: 300, orderBy: date, orderDirection: desc) {
        id
        date
        token0Price
        token1Price
      }
      poolHourData(first: 24, orderBy: periodStartUnix, orderDirection: desc) {
        id
        periodStartUnix
        token0Price
        token1Price
      }
    }
  }
`

type ChartRange = '1d' | '1w' | '1y' | 'all'
type PricePoint = { id: string; date: number; token0Price: number; token1Price: number }
type PriceHistoryData = {
  pool?: {
    token0Price: string
    token1Price: string
    poolDayData: Array<{ id: string; date: number; token0Price: string; token1Price: string }>
    poolHourData: Array<{ id: string; periodStartUnix: number; token0Price: string; token1Price: string }>
  } | null
}

const Card = styled.section`
  min-width: 0;
  padding: 20px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 16px;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`

const Price = styled.div`
  color: ${({ theme }) => theme.textPrimary};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.25;
`

const Caption = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
`

const ReverseButton = styled.button`
  flex: 0 0 auto;
  padding: 6px 9px;
  color: ${({ theme }) => theme.accentAction};
  background: ${({ theme }) => theme.accentActionSoft};
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    filter: brightness(0.96);
  }
`

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  min-height: 330px;
`

const Canvas = styled.div`
  position: relative;
  min-width: 0;
  height: 330px;
`

const Svg = styled.svg`
  width: 100%;
  height: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.backgroundOutline};

  .price-area {
    fill: url(#position-price-area);
  }

  .price-line {
    fill: none;
    stroke: #d86624;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2.4;
  }

  .position-range {
    fill: #8b8f97;
    fill-opacity: 0.33;
  }

  .range-boundary {
    stroke: #737780;
    stroke-dasharray: 3 3;
    stroke-width: 0.8;
  }
`

const Axis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 8px 0 20px 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 10px;
  text-align: right;
`

const HoverVertical = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => `${$left}%`};
  width: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`

const HoverHorizontal = styled.div<{ $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  right: 0;
  left: 0;
  height: 1px;
  background: ${({ theme }) => theme.backgroundOutline};
  pointer-events: none;
`

const HoverPoint = styled.div<{ $left: number; $top: number }>`
  position: absolute;
  top: ${({ $top }) => `${$top}%`};
  left: ${({ $left }) => `${$left}%`};
  width: 10px;
  height: 10px;
  background: #d86624;
  border: 2px solid ${({ theme }) => theme.backgroundSurface};
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d86624;
  pointer-events: none;
  transform: translate(-50%, -50%);
`

const HoverTip = styled.div<{ $left: number }>`
  position: absolute;
  top: 10px;
  left: ${({ $left }) => `${$left}%`};
  z-index: 2;
  padding: 6px 8px;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  font-size: 11px;
  pointer-events: none;
  transform: translateX(-50%);
  white-space: nowrap;
`

const RangeLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 11px;
`

const LegendSwatch = styled.span`
  width: 18px;
  height: 8px;
  background: rgba(139, 143, 151, 0.33);
  border: 1px dashed #737780;
  border-radius: 2px;
`

const RangeControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 2px;
  width: 75%;
  margin: 14px auto 0;
  padding: 3px;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 999px;
`

const RangeButton = styled.button<{ $active: boolean }>`
  flex: 1;
  min-width: 38px;
  padding: 6px 9px;
  color: ${({ $active, theme }) => ($active ? theme.textPrimary : theme.textSecondary)};
  background: ${({ $active, theme }) => ($active ? theme.backgroundModule : 'transparent')};
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    color: ${({ theme }) => theme.accentAction};
    background: ${({ theme }) => theme.accentActionSoft};
  }
`

const Empty = styled.div`
  display: grid;
  height: 330px;
  color: ${({ theme }) => theme.textSecondary};
  place-items: center;
`

const formatPrice = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString(undefined, { maximumSignificantDigits: 7 }) : '—'

const formatDate = (timestamp: number, hourly: boolean) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(hourly ? { hour: 'numeric' } : {}),
  }).format(new Date(timestamp * 1000))

export function selectPositionChartPrice(point: Pick<PricePoint, 'token0Price' | 'token1Price'>, inverted: boolean) {
  return inverted ? point.token0Price : point.token1Price
}

export default function PositionPriceHistoryChart({
  poolAddress,
  baseSymbol,
  quoteSymbol,
  inverted,
  onInvert,
  lowerPrice,
  upperPrice,
}: {
  poolAddress?: string
  baseSymbol?: string
  quoteSymbol?: string
  inverted: boolean
  onInvert: () => void
  lowerPrice?: number
  upperPrice?: number
}) {
  const [chartRange, setChartRange] = useState<ChartRange>('1y')
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)
  const { data, loading } = useQuery<PriceHistoryData>(POSITION_PRICE_HISTORY_QUERY, {
    client: analyticsApolloClient,
    variables: { id: poolAddress?.toLowerCase() ?? '' },
    skip: !poolAddress,
  })

  const dailyPoints = useMemo<PricePoint[]>(
    () =>
      [...(data?.pool?.poolDayData ?? [])].reverse().map((point) => ({
        id: point.id,
        date: Number(point.date),
        token0Price: Number(point.token0Price),
        token1Price: Number(point.token1Price),
      })),
    [data]
  )
  const hourlyPoints = useMemo<PricePoint[]>(
    () =>
      [...(data?.pool?.poolHourData ?? [])].reverse().map((point) => ({
        id: point.id,
        date: Number(point.periodStartUnix),
        token0Price: Number(point.token0Price),
        token1Price: Number(point.token1Price),
      })),
    [data]
  )
  const points =
    chartRange === '1d'
      ? hourlyPoints
      : chartRange === '1w'
      ? dailyPoints.slice(-7)
      : chartRange === '1y'
      ? dailyPoints.slice(-365)
      : dailyPoints
  const values = points.map((point) => selectPositionChartPrice(point, inverted)).filter(Number.isFinite)
  const observedMinimum = Math.min(...values)
  const observedMaximum = Math.max(...values)
  const spread = observedMaximum - observedMinimum || Math.max(observedMaximum * 0.02, 1e-9)
  const chartMinimum = Math.max(0, observedMinimum - spread * 0.08)
  const chartMaximum = observedMaximum + spread * 0.08
  const chartSpread = chartMaximum - chartMinimum || 1
  const yForValue = (value: number) => 96 - ((value - chartMinimum) / chartSpread) * 92
  const linePoints = values
    .map((value, index) => `${values.length === 1 ? 50 : (index / (values.length - 1)) * 100},${yForValue(value)}`)
    .join(' ')
  const areaPoints = `0,100 ${linePoints} 100,100`
  const rangeTop = upperPrice === undefined ? 4 : Math.max(4, Math.min(96, yForValue(upperPrice)))
  const rangeBottom = lowerPrice === undefined ? 96 : Math.max(4, Math.min(96, yForValue(lowerPrice)))
  const rangeHeight = lowerPrice === undefined && upperPrice === undefined ? 92 : Math.max(4, rangeBottom - rangeTop)
  const axisValues = Array.from({ length: 6 }, (_, index) => chartMaximum - (chartSpread / 5) * index)
  const hoveredValue = hoverIndex === null ? undefined : values[hoverIndex]
  const hoveredPoint = hoverIndex === null ? undefined : points[hoverIndex]
  const hoverLeft = hoverIndex === null ? 0 : (hoverIndex / Math.max(values.length - 1, 1)) * 100
  const hoverTop = hoveredValue === undefined ? 0 : yForValue(hoveredValue)
  const currentPrice = Number(inverted ? data?.pool?.token0Price : data?.pool?.token1Price)
  const latestValue = Number.isFinite(currentPrice) && currentPrice > 0 ? currentPrice : values[values.length - 1]

  return (
    <Card>
      <Header>
        <div>
          <Price>
            1 {baseSymbol} = {formatPrice(latestValue)} {quoteSymbol}
          </Price>
          <Caption>Indexed pool price history</Caption>
        </div>
        <ReverseButton type="button" onClick={onInvert}>
          ⇄ Reverse price
        </ReverseButton>
      </Header>
      {loading ? (
        <Empty>Loading price history…</Empty>
      ) : values.length ? (
        <ChartGrid>
          <Canvas
            onMouseLeave={() => setHoverIndex(null)}
            onMouseMove={(event) => {
              const bounds = event.currentTarget.getBoundingClientRect()
              const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width))
              setHoverIndex(Math.round(ratio * Math.max(values.length - 1, 0)))
            }}
          >
            <Svg viewBox="0 0 100 100" preserveAspectRatio="none" role="img" aria-label="Position price history">
              <defs>
                <linearGradient id="position-price-area" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F6A04F" stopOpacity="0.99" />
                  <stop offset="100%" stopColor="#F6A04F" stopOpacity="0.01" />
                </linearGradient>
              </defs>
              <polygon className="price-area" points={areaPoints} />
              <rect className="position-range" x="0" y={rangeTop} width="100" height={rangeHeight} />
              <line className="range-boundary" x1="0" x2="100" y1={rangeTop} y2={rangeTop} />
              <line className="range-boundary" x1="0" x2="100" y1={rangeBottom} y2={rangeBottom} />
              <polyline className="price-line" points={linePoints} vectorEffect="non-scaling-stroke" />
            </Svg>
            {hoverIndex !== null && hoveredValue !== undefined && (
              <>
                <HoverVertical $left={hoverLeft} />
                <HoverHorizontal $top={hoverTop} />
                <HoverPoint $left={hoverLeft} $top={hoverTop} />
                <HoverTip $left={hoverLeft}>
                  {hoveredPoint ? formatDate(hoveredPoint.date, chartRange === '1d') : 'Current'}:{' '}
                  <strong>
                    {formatPrice(hoveredValue)} {quoteSymbol}
                  </strong>
                </HoverTip>
              </>
            )}
          </Canvas>
          <Axis>
            {axisValues.map((value, index) => (
              <span key={index}>{formatPrice(value)}</span>
            ))}
          </Axis>
        </ChartGrid>
      ) : (
        <Empty>Price history is not indexed for this pool yet.</Empty>
      )}
      <RangeLegend>
        <LegendSwatch />
        Position range: {lowerPrice === undefined ? 'Min' : formatPrice(lowerPrice)} –{' '}
        {upperPrice === undefined ? 'Max' : formatPrice(upperPrice)} {quoteSymbol} per {baseSymbol}
      </RangeLegend>
      <RangeControls role="tablist" aria-label="Position chart time range">
        {(['1d', '1w', '1y', 'all'] as const).map((range) => (
          <RangeButton
            key={range}
            type="button"
            role="tab"
            aria-selected={chartRange === range}
            $active={chartRange === range}
            onClick={() => setChartRange(range)}
          >
            {range === 'all' ? 'All time' : range.toUpperCase()}
          </RangeButton>
        ))}
      </RangeControls>
    </Card>
  )
}
